import React from 'react';
import styled from 'styled-components';
import { Border, Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import { Container, InlineList } from '../../../components/Layout';
import { IconBadge, RoundedSecondaryBadge } from '../../../components/Badge';
import TrainingHeaderActions from './TrainingHeaderActions';
import capitalizeFilter from '../../../filters/capitalize-filter';
import TrainingPosterPlaceholder from './TrainingPosterPlaceholder';
import WhiteLabel from '../../../filters/WhiteLabel';
import { useUser } from '../../authentication';
import trainingService from '../../../services/trainingService';
import TrainingOverview from './TrainingOverview';
import { Training } from '../models/training';
import { NextAction } from '../models/nextAction';
import { UserTrainingAccess } from '../models/UserTrainingAccess';
import { isPathway } from '../utils/isPathway';
import { isCourse } from '../utils/isCourse';

const TrainingHeaderContainer = styled(Container)`
  width: 100%;
  margin: ${Grid._6} 0;
  align-self: center;
`;

const TrainingPosterContainer = styled.div`
  height: fit-content;
  position: relative;
`;

export const TrainingPoster = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: ${Border.radius};
  background: ${Color.Gray._20};
  box-shadow: ${Shadow.light};
`;

const TrainingInformation = styled.div<{ isEmptyDescription?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: ${props => (props.isEmptyDescription ? 'auto' : '100%')};
  gap: ${Grid._3};
`;

const TrainingDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._3};
`;

const TrainingName = styled.h1`
  margin: 0;
  color: ${Type.Color.dark};
  font-size: ${Type.Scale._5};
  font-weight: ${Type.Weight.bold};
  line-height: ${Type.Leading.normal};
`;

const PathwayBadge = styled(IconBadge)`
  position: absolute;
  bottom: -25px;
  right: ${Grid._3};
`;

export const Labels = styled.div<{ fontSize?: string }>`
  display: flex;
  justify-content: flex-start !important;
  align-items: center;
  margin: 0;
  color: ${Type.Color.medium};
  font-size: ${props => (props.fontSize ? props.fontSize : Type.Scale._4)};
  text-transform: uppercase;
  span {
    margin-left: ${Grid._3};
    text-transform: none;
  }
`;

const Categories = ({ categories }: { categories: string[] }) => {
  return categories.map((category, i) => (
    <li key={i}>
      <i className="fas fa-tag" style={{ marginRight: Grid._2 }}></i>
      {category}
    </li>
  ));
};

const TrainingHeader = ({
  training,
  nextAction,
  userAccess,
}: {
  training: Training;
  nextAction?: NextAction;
  userAccess: UserTrainingAccess;
}) => {
  const { user } = useUser();
  const { hasGridSubscription, hasIndividualGridSubscription } = userAccess;

  const numOfItems = isCourse(training)
    ? training.tasks.length
    : training.entities.filter(e => e._type === 'TaskList').length;

  return (
    <TrainingHeaderContainer className="grid-container grid-col-4 grid-sm-col-8 grid-gap-24">
      <TrainingPosterContainer className="grid-col-span-4 grid-sm-col-span-3">
        {training.posterImage ? (
          <TrainingPoster src={trainingService.getTrainingImageUrl(training.posterImage)} alt={training.name} />
        ) : (
          <TrainingPosterPlaceholder ownerName={training.ownerName} />
        )}
        {isPathway(training) && (
          <PathwayBadge>
            <i className="fas fa-route" />
          </PathwayBadge>
        )}
      </TrainingPosterContainer>
      <div className="grid-col-span-4 grid-sm-col-span-5">
        <TrainingInformation isEmptyDescription={!training.description}>
          <TrainingDetailsContainer>
            <TrainingName>
              {training.name}{' '}
              {training.searchAccess === 'Free' &&
                (!user || (!hasGridSubscription && !hasIndividualGridSubscription)) && (
                  <RoundedSecondaryBadge style={{ verticalAlign: 'middle' }} className="rounded-secondary-badge">
                    Free
                  </RoundedSecondaryBadge>
                )}
            </TrainingName>
            <InlineList content="•" fontSize="14px">
              <li>
                {numOfItems}{' '}
                {training.type === 'course'
                  ? capitalizeFilter(WhiteLabel.labels.step)
                  : capitalizeFilter(WhiteLabel.labels.course)}
                {numOfItems > 1 && 's'}
              </li>
              {training.ownerName && <li>{training.ownerName}</li>}
              <Categories categories={training.categories} />
            </InlineList>
            <TrainingOverview description={training.description} maxLines={2} />
          </TrainingDetailsContainer>

          <TrainingHeaderActions training={training} userAccess={userAccess} nextAction={nextAction} />
        </TrainingInformation>
      </div>
    </TrainingHeaderContainer>
  );
};

export default TrainingHeader;
